import { useEffect, useMemo, useState } from "react";
import Indicator from "../../../../../components/base/Indicator";
import { useHub } from "../../../../../core/Global";
import { WalletDateRange } from "../WalletDateFilter";
import { ChartSeries, fmtDuration, fmtMinutes, num, pct, UsageAllowanceCards, UsageDailyChart, UsagePeriod, UsageRow, UsageStatCard, useUsageHeader } from "./UsageCommon";


type Bucket = { calls: number; answered: number; seconds: number; billedMinutes: number }

type Day = { day: string; inboundMinutes: number; outboundMinutes: number; internalMinutes: number; missed: number }

type CallsUsageData = {
    start: string
    end: string
    report: {
        totalCalls: number
        totalSeconds: number
        billedMinutes: number
        answered: number
        missed: number
        outboundUnanswered: number
        busy: number
        failed: number
        recorded: number
        longestSeconds: number
        pbx: Bucket
        proxy: Bucket
        inbound: Bucket
        outbound: Bucket
        internal: Bucket
        days: Day[]
    }
    fromAllowance: number
    fromWallet: { count: number; minutes: number; amount: number }
    period?: UsagePeriod | null
}


export default function CallsUsage() {

    const hub = useHub()

    const [range, rangeSet] = useState<WalletDateRange>()
    const [data, dataSet] = useState<CallsUsageData>()
    const [fetching, fetchingSet] = useState<boolean>(false)

    const mobileFilter = useUsageHeader("Calls", "usage-calls-date", rangeSet)

    useEffect(() => {
        if (!range) return
        fetchingSet(true)
        hub.Get(`/accounts/profile/usage/calls?start=${range.start}&end=${range.end}`, res => {
            if (res?.success && res.data) dataSet(res.data)
            fetchingSet(false)
        })
    }, [range?.start, range?.end])

    const r = data?.report

    const chart = useMemo(() => {
        const days = r?.days ?? []
        const series: ChartSeries[] = [
            { name: "Inbound", data: days.map(d => d.inboundMinutes), color: "--bs-primary" },
            { name: "Outbound", data: days.map(d => d.outboundMinutes), color: "--bs-info" },
            { name: "Internal", data: days.map(d => d.internalMinutes), color: "--bs-gray-400" },
        ]
        return { days: days.map(d => d.day), series }
    }, [r])

    const caption = range?.label ? `In ${range.label}` : "In selected period"
    const avgSeconds = r && r.answered > 0 ? r.totalSeconds / r.answered : 0

    return (
        <div>
            <div className="px-9 py-5">
                {mobileFilter}
                {
                    !data || !r ?
                        <div className="text-center py-15"><Indicator /></div>
                        :
                        <div style={{ opacity: fetching ? .6 : 1, transition: "opacity .2s" }}>

                            <div className="row g-5 mb-5">
                                <div className="col-sm-6 col-xl-3">
                                    <UsageStatCard title="Total Talk Time" icon="clock" color="primary"
                                        value={fmtDuration(r.totalSeconds)} caption={caption}>
                                        <UsageRow label="Calls" value={num(r.totalCalls)} />
                                        <UsageRow label="Billed minutes" value={num(r.billedMinutes)} />
                                    </UsageStatCard>
                                </div>
                                <div className="col-sm-6 col-xl-3">
                                    <UsageStatCard title="Through PBX" icon="server" color="info"
                                        value={fmtDuration(r.pbx.seconds)} caption="Lines, queues & extensions">
                                        <UsageRow label="Calls" value={num(r.pbx.calls)} />
                                        <UsageRow label="Billed minutes" value={num(r.pbx.billedMinutes)} />
                                    </UsageStatCard>
                                </div>
                                <div className="col-sm-6 col-xl-3">
                                    <UsageStatCard title="Through Extension" icon="headset" color="warning"
                                        value={fmtDuration(r.proxy.seconds)} caption="Technician proxy numbers">
                                        <UsageRow label="Calls" value={num(r.proxy.calls)} />
                                        <UsageRow label="Billed minutes" value={num(r.proxy.billedMinutes)} />
                                    </UsageStatCard>
                                </div>
                                <div className="col-sm-6 col-xl-3">
                                    <UsageStatCard title="Answer Rate" icon="phone-volume" color="success"
                                        value={`${pct(r.answered, r.totalCalls)}%`} caption={`${num(r.answered)} of ${num(r.totalCalls)} calls answered`}>
                                        <UsageRow label="Missed (inbound)" value={<span className="text-danger">{num(r.missed)}</span>} />
                                        <UsageRow label="Not picked up (outbound)" value={num(r.outboundUnanswered)} />
                                    </UsageStatCard>
                                </div>
                            </div>

                            <div className="row g-5 mb-5">
                                <div className="col-sm-6 col-xl-4">
                                    <UsageStatCard title="Inbound" icon="phone-arrow-down-left" color="primary"
                                        value={fmtDuration(r.inbound.seconds)} caption={caption}>
                                        <UsageRow label="Calls" value={num(r.inbound.calls)} />
                                        <UsageRow label="Answered" value={num(r.inbound.answered)} />
                                        <UsageRow label="Missed" value={num(r.inbound.calls - r.inbound.answered)} />
                                    </UsageStatCard>
                                </div>
                                <div className="col-sm-6 col-xl-4">
                                    <UsageStatCard title="Outbound" icon="phone-arrow-up-right" color="info"
                                        value={fmtDuration(r.outbound.seconds)} caption={caption}>
                                        <UsageRow label="Calls" value={num(r.outbound.calls)} />
                                        <UsageRow label="Connected" value={num(r.outbound.answered)} />
                                        <UsageRow label="Not picked up" value={num(r.outbound.calls - r.outbound.answered)} />
                                    </UsageStatCard>
                                </div>
                                <div className="col-sm-12 col-xl-4">
                                    <UsageStatCard title="Call Quality" icon="stopwatch" color="danger"
                                        value={fmtDuration(avgSeconds)} caption="Average answered call">
                                        <UsageRow label="Longest call" value={fmtDuration(r.longestSeconds)} />
                                        <UsageRow label="Busy / failed" value={`${num(r.busy)} / ${num(r.failed)}`} />
                                        <UsageRow label="Recorded calls" value={num(r.recorded)} />
                                        <UsageRow label="Internal (ext. to ext.)" value={`${num(r.internal.calls)} · ${fmtDuration(r.internal.seconds)}`} />
                                    </UsageStatCard>
                                </div>
                            </div>

                            <UsageAllowanceCards
                                unit="minutes"
                                fromAllowance={data.fromAllowance}
                                fromWallet={data.fromWallet.minutes}
                                walletAmount={data.fromWallet.amount}
                                period={data.period}
                                rangeLabel={range?.label}
                                formatValue={v => `${fmtMinutes(v)}`} />

                            <UsageDailyChart
                                title="Call Minutes per Day"
                                subtitle={`${fmtDuration(r.totalSeconds)} talk time${range?.label ? ` · ${range.label}` : ""}`}
                                days={chart.days}
                                series={chart.series}
                                stacked
                                valueFormatter={v => `${num(Math.round(v * 10) / 10)} min`} />
                        </div>
                }
            </div>
        </div>
    )
}
